// App.js

import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useMaterialUIController } from "context";
import CssBaseline from "@mui/material/CssBaseline";
import MDBox from "components/MDBox";
import DashboardNavbar from "common/Navbars/DashboardNavbar";
import routes, { getRoutes } from "routes";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import { ThemeProvider } from "@mui/material/styles";
import { useAuth } from "context/AuthContext";
import { useLanguage } from "context/LanguageContext";
import Loader from "components/Loader/Loader";

function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <Loader fullscreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/authentication/sign-in" state={{ from: location }} replace />;
  }

  return children;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

function AppContent() {
  const [controller, dispatch] = useMaterialUIController();
  const { direction, layout, sidenavColor, transparentSidenav, whiteSidenav, darkMode } =
    controller;
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useLanguage();

  // Get translated routes
  const translatedRoutes = useMemo(() => getRoutes(t), [t]);

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const renderRoutes = (allRoutes) =>
    allRoutes.flatMap((route) => {
      if (route.collapse) {
        return renderRoutes(route.collapse);
      }

      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={
              route.protected ? <ProtectedRoute>{route.component}</ProtectedRoute> : route.component
            }
            key={route.key}
          />
        );
      }

      return [];
    });

  // Floating settings button removed to avoid duplication with header settings icon

  if (isLoading) {
    return <Loader fullscreen />;
  }

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && isAuthenticated && (
          <>
            <DashboardNavbar />
          </>
        )}
        <Routes>
          {renderRoutes(translatedRoutes)}
          {!isAuthenticated && (
            <Route path="/" element={<Navigate to="/authentication/sign-in" replace />} />
          )}
          {isAuthenticated && <Route path="/" element={<Navigate to="/documents" replace />} />}
          {/* Galite pridėti NotFound komponentą, jei reikia */}
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && isAuthenticated && (
        <>
          <DashboardNavbar />
        </>
      )}
      <Routes>
        {renderRoutes(translatedRoutes)}
        {!isAuthenticated && (
          <Route path="/" element={<Navigate to="/authentication/sign-in" replace />} />
        )}
        {isAuthenticated && <Route path="/" element={<Navigate to="/documents" replace />} />}
        {/* Galite pridėti NotFound komponentą, jei reikia */}
      </Routes>
    </ThemeProvider>
  );
}

export default AppContent;
